<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import AuditoriaAPI from 'dashboard/api/fandata/auditoria';

// Registro de auditoria da configuração (26/08). Nasceu quando o rastro do enterprise saiu junto
// com o DISABLE_ENTERPRISE, e responde à pergunta "quem mudou a resposta do Bira na semana
// passada", que é de operação e também de LGPD.
//
// Só leitura, de propósito: não existe editar nem apagar aqui. Registro que a aplicação sabe
// alterar não serve como evidência.
const { t } = useI18n();

const isFetching = ref(true);
const registros = ref([]);
const acoes = ref([]);
const total = ref(0);
const acaoFiltro = ref('');
const pagina = ref(1);

const POR_PAGINA = 50;
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(total.value / POR_PAGINA))
);

const carregar = async () => {
  isFetching.value = true;
  try {
    const { data } = await AuditoriaAPI.listar({
      acao: acaoFiltro.value || undefined,
      page: pagina.value,
    });
    registros.value = data.registros || [];
    // A lista de ações vem do backend e não é fixa no front: ação nova passa a aparecer no
    // filtro sozinha, sem precisar de deploy do dashboard.
    acoes.value = data.acoes || [];
    total.value = data.total || 0;
  } catch (e) {
    useAlert(t('CAPITAO_SETTINGS.AUDITORIA.ERRO'));
  } finally {
    isFetching.value = false;
  }
};

const filtrar = () => {
  pagina.value = 1;
  carregar();
};

const irPara = destino => {
  pagina.value = Math.min(Math.max(1, destino), totalPaginas.value);
  carregar();
};

const formatarData = valor => {
  if (!valor) return '';
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Rótulo em português para a ação. Ação desconhecida cai no próprio código, em vez de sumir da
// tela: log incompleto é pior do que log feio.
const ROTULOS = {
  'calibracao.atualizou': 'Calibração alterada',
  'base.criou': 'Base: item criado',
  'base.atualizou': 'Base: item alterado',
  'base.removeu': 'Base: item removido',
  'parceiro.criou': 'Parceiro criado',
  'parceiro.atualizou': 'Parceiro alterado',
  'parceiro.removeu': 'Parceiro removido',
  'convite.gerou': 'Link de convite gerado',
  'sandbox.publicou': 'Calibração publicada',
  'sandbox.reverteu': 'Calibração revertida',
};
const rotulo = acao => ROTULOS[acao] || acao;

// Detalhe legível sem virar despejo de JSON na tela.
const resumoDetalhes = registro => {
  const d = registro.detalhes || {};
  if (Array.isArray(d.campos) && d.campos.length) {
    return `campos: ${d.campos.join(', ')}`;
  }
  if (d.para_usuario) return `para o usuário ${d.para_usuario}`;
  if (d.nome) return d.nome;
  const chaves = Object.keys(d);
  return chaves.length ? chaves.map(k => `${k}: ${d[k]}`).join(' · ') : '';
};

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('CAPITAO_SETTINGS.AUDITORIA.HEADER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CAPITAO_SETTINGS.AUDITORIA.HEADER')"
        :description="$t('CAPITAO_SETTINGS.AUDITORIA.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4 mt-4">
        <div class="flex items-center gap-3">
          <label class="text-sm text-n-slate-11">
            {{ $t('CAPITAO_SETTINGS.AUDITORIA.FILTRO') }}
          </label>
          <select
            v-model="acaoFiltro"
            class="px-2 py-1.5 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="filtrar"
          >
            <option value="">
              {{ $t('CAPITAO_SETTINGS.AUDITORIA.TODAS') }}
            </option>
            <option v-for="a in acoes" :key="a" :value="a">
              {{ rotulo(a) }}
            </option>
          </select>
          <span class="text-sm text-n-slate-11">
            {{ $t('CAPITAO_SETTINGS.AUDITORIA.TOTAL', { total }) }}
          </span>
        </div>

        <div
          v-if="!registros.length"
          class="p-6 text-sm text-center border rounded-xl border-n-weak text-n-slate-11"
        >
          {{ $t('CAPITAO_SETTINGS.AUDITORIA.VAZIO') }}
        </div>

        <div
          v-else
          class="border rounded-xl border-n-weak divide-y divide-n-weak"
        >
          <div
            v-for="registro in registros"
            :key="registro.id"
            class="flex flex-col gap-1 px-4 py-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ rotulo(registro.acao) }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ registro.autor }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ formatarData(registro.em) }}
              </span>
            </div>
            <div
              v-if="resumoDetalhes(registro) || registro.alvo"
              class="text-xs text-n-slate-11"
            >
              <span v-if="registro.alvo">{{ registro.alvo }}</span>
              <span v-if="registro.alvo && resumoDetalhes(registro)"> · </span>
              <span v-if="resumoDetalhes(registro)">
                {{ resumoDetalhes(registro) }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="totalPaginas > 1"
          class="flex items-center justify-end gap-2"
        >
          <Button
            faded
            slate
            sm
            :label="$t('CAPITAO_SETTINGS.AUDITORIA.ANTERIOR')"
            :disabled="pagina <= 1"
            @click="irPara(pagina - 1)"
          />
          <span class="text-xs text-n-slate-11">
            {{ pagina }} / {{ totalPaginas }}
          </span>
          <Button
            faded
            slate
            sm
            :label="$t('CAPITAO_SETTINGS.AUDITORIA.PROXIMA')"
            :disabled="pagina >= totalPaginas"
            @click="irPara(pagina + 1)"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
